import { useState, useEffect, useRef, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api.ts'

// Single-prospect letter-draft state extracted from LetterReviewCard.
// Initial generation goes through POST /acquisition/prospects/:id/write-letter
// (server caches drafts per prospect). Manual edits flow through either
// PUT /acquisition/letters/:id/body (if we already have an ID) or
// POST /acquisition/prospects/:id/letter-draft (first edit of a generated draft).

export interface UseLetterDraftResult {
  letterText: string | null
  letterId: string | null
  confidence: number | null
  warnings: string[]
  isGenerating: boolean
  error: string | null

  // Inline manual editor
  isEditing: boolean
  editedText: string
  setEditedText: (text: string) => void
  startEditing: () => void
  cancelEditing: () => void
  saveDraft: () => Promise<void>
  savingDraft: boolean
  saveError: string | null

  regenerate: () => void
}

export function useLetterDraft(
  prospectId: string,
  referenceCustomerId?: string | null,
): UseLetterDraftResult {
  const queryClient = useQueryClient()

  const [letterText, setLetterText] = useState<string | null>(null)
  const [letterId, setLetterId] = useState<string | null>(null)
  const [confidence, setConfidence] = useState<number | null>(null)
  const [warnings, setWarnings] = useState<string[]>([])
  const [isGenerating, setIsGenerating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [isEditing, setIsEditing] = useState(false)
  const [editedText, setEditedText] = useState('')
  const [savingDraft, setSavingDraft] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  // genVersion guards against stale network responses overwriting fresh state
  // when the user switches prospects or regenerates mid-flight.
  const genVersionRef = useRef(0)

  const runGeneration = useCallback((force: boolean) => {
    const version = genVersionRef.current + 1
    genVersionRef.current = version

    setIsGenerating(true)
    setError(null)
    if (force) {
      setLetterText(null)
      setConfidence(null)
      setWarnings([])
    }

    const payload: Record<string, unknown> = {}
    if (force) payload.force = true
    if (referenceCustomerId) payload.referenceCustomerId = referenceCustomerId

    apiClient
      .post(`/acquisition/prospects/${prospectId}/write-letter`, payload)
      .then(({ data }) => {
        if (genVersionRef.current !== version) return
        setLetterText(data.letterText)
        if (data.letterId) setLetterId(data.letterId)
        setConfidence(typeof data.confidence === 'number' ? data.confidence : null)
        setWarnings(Array.isArray(data.warnings) ? data.warnings : [])
      })
      .catch((err) => {
        if (genVersionRef.current !== version) return
        const msg = err?.response?.data?.error || 'Fehler bei der Briefgenerierung.'
        setError(msg)
      })
      .finally(() => {
        if (genVersionRef.current === version) setIsGenerating(false)
      })
  }, [prospectId, referenceCustomerId])

  // Reset + generate whenever the target prospect changes.
  useEffect(() => {
    genVersionRef.current += 1
    setLetterText(null)
    setLetterId(null)
    setConfidence(null)
    setWarnings([])
    setError(null)
    setIsEditing(false)
    setEditedText('')
    setSaveError(null)
    runGeneration(false)
  }, [prospectId, referenceCustomerId, runGeneration])

  const startEditing = useCallback(() => {
    setEditedText(letterText ?? '')
    setSaveError(null)
    setIsEditing(true)
  }, [letterText])

  const cancelEditing = useCallback(() => {
    setIsEditing(false)
    setEditedText('')
    setSaveError(null)
  }, [])

  const saveDraft = useCallback(async () => {
    const trimmed = editedText.trim()
    if (trimmed.length < 1) {
      setSaveError('Brieftext darf nicht leer sein')
      return
    }
    setSavingDraft(true)
    setSaveError(null)
    try {
      let savedText = editedText
      let savedId = letterId
      if (letterId) {
        const { data } = await apiClient.put(`/acquisition/letters/${letterId}/body`, {
          letterText: editedText,
        })
        savedText = data?.letterText ?? editedText
        savedId = data?.letterId ?? letterId
      } else {
        const payload: Record<string, unknown> = { letterText: editedText }
        if (referenceCustomerId) payload.referenceCustomerId = referenceCustomerId
        const { data } = await apiClient.post(
          `/acquisition/prospects/${prospectId}/letter-draft`,
          payload,
        )
        savedText = data?.letterText ?? editedText
        savedId = data?.letterId ?? null
      }
      setLetterText(savedText)
      if (savedId) setLetterId(savedId)
      // Manual edits invalidate the AI-generated confidence / warnings.
      setConfidence(null)
      setWarnings([])
      setIsEditing(false)
      setEditedText('')
      queryClient.invalidateQueries({ queryKey: ['prospect-letters', prospectId] })
    } catch (err: unknown) {
      const axErr = err as { response?: { data?: { message?: string; error?: string } }; message?: string }
      const msg =
        axErr?.response?.data?.message ||
        axErr?.response?.data?.error ||
        axErr?.message ||
        'Unbekannter Fehler beim Speichern'
      setSaveError(msg)
    } finally {
      setSavingDraft(false)
    }
  }, [editedText, letterId, prospectId, queryClient, referenceCustomerId])

  const regenerate = useCallback(() => {
    cancelEditing()
    runGeneration(true)
  }, [cancelEditing, runGeneration])

  return {
    letterText,
    letterId,
    confidence,
    warnings,
    isGenerating,
    error,
    isEditing,
    editedText,
    setEditedText,
    startEditing,
    cancelEditing,
    saveDraft,
    savingDraft,
    saveError,
    regenerate,
  }
}
